import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as yup from "yup";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { UserPlus } from "lucide-react";
import jobLogo from "../images/Logo.svg";

const EmployeeRegistrationForm = () => {
  const [step] = useState(0);
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState("user");
  const [successMsg, setSuccessMsg] = useState("");

  const REACT_APP_API_URL = process.env.REACT_APP_API_URL;

  /* ================= VALIDATION ================= */

  const validationSchemaStep0 = yup.object({
    name: yup.string().required("Full Name is required"),
    email: yup.string().email("Invalid email format").required("Email is required"),
    phone_number: yup
      .string()
      .matches(/^[0-9]{10}$/, "Enter a valid 10-digit mobile number")
      .required("Mobile number is required"),
    resume: yup
      .mixed()
      .test(
        "fileFormat",
        "Invalid file type. Allowed: DOC, PDF, JPG, PNG.",
        (file) => {
          if (!file) return true;
          const allowedExtensions = ["doc", "docx", "pdf", "jpg", "jpeg", "png"];
          return allowedExtensions.includes(
            file?.name?.split(".").pop().toLowerCase()
          );
        }
      )
      .test(
        "fileSize",
        "File size exceeds 5MB limit.",
        (file) => {
          if (!file) return true;
          return file.size <= 5 * 1024 * 1024;
        }
      ),
  });

  const initialValues = {
    name: "",
    email: "",
    phone_number: "",
    resume: "",
    otp1: "",
    password: "",
    confirmPassword: "",
  };

  /* ================= STEP HANDLER ================= */

  const handleNext = async (values, validateForm, setErrors, setSubmitting) => {
    setSubmitting(true);
    try {
      const errors = await validateForm();
      if (Object.keys(errors).length > 0) {
        setErrors(errors);
        setSubmitting(false);
        return;
      }

      if (step === 0) {
        const formData = new FormData();
        formData.append("name", values.name);
        formData.append("phone_number", values.phone_number);
        formData.append("email", values.email);
        formData.append("resume", values.resume || "");

        const response = await axios.post(
          `${REACT_APP_API_URL}/api/auth/register-user`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        if (response.data?.message) {
          setSuccessMsg(response.data.message);
          setTimeout(() => navigate("/login"), 3000);
        } else {
          setErrors({ api: "Registration failed" });
        }
      }
    } catch (error) {
      setErrors({
        api: error.response?.data?.message || "An error occurred",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getValidationSchema = () => {
    if (step === 0) return validationSchemaStep0;
    return yup.object({});
  };

  /* ================= UI ================= */

  return (
    <div
      className="min-h-screen w-full font-['Inter',sans-serif] flex items-center justify-center p-4 md:py-8 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >

      {/* Decorative background shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)' }}
        />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)' }}
        />
      </div>

      {/* Card */}
      <div
        className="relative w-full !max-w-lg bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-8 z-10"
        style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.3)' }}
      >
        {/* Branding */}
        <div className="flex flex-col items-center mb-6">
          <img src={jobLogo} alt="Logo" className="h-16 mb-4 cursor-pointer" onClick={() => navigate("/")} />
          <h1 className="text-2xl font-bold text-gray-800 tracking-tight">Create Account</h1>
          <p className="text-gray-500 text-sm mt-1">Join us and find your dream teaching job</p>
        </div>

        {/* Register toggle */}
        <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
          <button
            type="button"
            onClick={() => {
              setSelectedType("user");
              navigate("/user");
            }}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-300 border-none cursor-pointer ${selectedType === "user"
              ? "bg-white text-blue-600 shadow-md"
              : "bg-transparent text-gray-500 hover:text-gray-700"
              }`}
          >
            Register as User
          </button>
          <button
            type="button"
            onClick={() => {
              setSelectedType("institution");
              navigate("/inst");
            }}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-300 border-none cursor-pointer ${selectedType === "institution"
              ? "bg-white text-blue-600 shadow-md"
              : "bg-transparent text-gray-500 hover:text-gray-700"
              }`}
          >
            Register as Institution
          </button>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={getValidationSchema()}
          onSubmit={(values, actions) =>
            handleNext(
              values,
              actions.validateForm,
              actions.setErrors,
              actions.setSubmitting
            )
          }
        >
          {({ setFieldValue, errors, isSubmitting }) => (
            <Form>
              {successMsg && (
                <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4 text-center">
                  {successMsg} Redirecting to login...
                </div>
              )}

              {errors.api && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4 text-center">
                  {errors.api}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  Full Name
                </label>
                <Field
                  name="name"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 text-sm placeholder-gray-400 outline-none transition-all duration-200 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100"
                  placeholder="Enter your full name"
                />
                <ErrorMessage name="name" component="div" className="text-red-500 text-xs mt-1.5 ml-1" />
              </div>

              <div className="mb-4">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  Email Address
                </label>
                <Field
                  name="email"
                  type="email"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 text-sm placeholder-gray-400 outline-none transition-all duration-200 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100"
                  placeholder="Enter your email"
                />
                <ErrorMessage name="email" component="div" className="text-red-500 text-xs mt-1.5 ml-1" />
              </div>

              <div className="mb-4">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  Phone Number
                </label>
                <Field
                  name="phone_number"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 text-sm placeholder-gray-400 outline-none transition-all duration-200 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100"
                  placeholder="Enter 10-digit phone number"
                />
                <ErrorMessage
                  name="phone_number"
                  component="div"
                  className="text-red-500 text-xs mt-1.5 ml-1"
                />
              </div>

              <div className="mb-6">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  Upload Resume <span className="text-gray-400 normal-case font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept=".doc,.docx,.pdf,.jpg,.jpeg,.png"
                    onChange={(e) =>
                      setFieldValue("resume", e.currentTarget.files[0])
                    }
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 border-dashed rounded-xl text-gray-600 text-sm outline-none transition-all duration-200 focus:border-blue-500 focus:bg-white file:mr-4 file:py-1.5 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100"
                  />
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 text-white font-semibold text-sm rounded-xl border-none cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                <UserPlus size={18} />
                {isSubmitting ? "Creating Account..." : "Create Account"}
              </button>

              {/* Login option */}
              <div className="mt-6 text-center">
                <span className="text-sm text-gray-500">
                  Already have an account?{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                    className="text-blue-600 font-semibold bg-transparent border-none cursor-pointer hover:text-blue-700 transition-colors"
                  >
                    Sign In
                  </button>
                </span>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default EmployeeRegistrationForm;